/**
 * What a hosted agent's tools did on one call, as the dashboard ran them.
 *
 * THE DASHBOARD'S OWN RECORD, NOT THE AGENT'S. A tool on a hosted agent runs on the Worker, so
 * this is what was actually asked of somebody's system and what the model was handed back -
 * including the runs that failed and the arguments the model got wrong, which are the ones
 * worth reading when a call went sideways. A custom agent's tools are its own code and show on
 * the transcript's timeline instead; for those this draws nothing.
 *
 * ARGUMENTS ARE NAME/VALUE PAIRS AND THE ANSWER IS TEXT, because the names are the tool
 * author's and a member's response has keys like `rate` removed on the way out.
 */

import type { ToolCall } from "../api";
import { Chip, cx } from "../ui";

const STATUS: Record<ToolCall["status"], { label: string; tone: string }> = {
  ok: { label: "Done", tone: "border-good-line bg-good-soft text-good" },
  error: { label: "Failed", tone: "border-alert-line bg-alert-soft text-alert" },
  rejected: { label: "Arguments refused", tone: "border-warm-line bg-warm-soft text-warm" },
  running: { label: "Did not finish", tone: "" },
};

export function ToolCalls({ calls }: { calls: ToolCall[] }) {
  if (!calls.length) return null;
  return (
    <section className="mt-3.5 border-t border-line-soft pt-3.5">
      <h3 className="mb-2 text-2xs font-semibold tracking-[0.06em] text-ink-soft uppercase">Tools</h3>
      <ol className="grid gap-2.5">
        {calls.map((call, index) => (
          <li key={index} className="rounded-sm border border-line-soft p-2.5 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-ink">{call.toolName}</span>
              <Chip className={cx(STATUS[call.status].tone)}>{STATUS[call.status].label}</Chip>
              {call.durationMs !== null && <span className="nums text-xs text-ink-faint">{call.durationMs} ms</span>}
            </div>
            {call.args.length > 0 && (
              <dl className="mt-2 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
                {call.args.map((arg) => (
                  <div key={arg.name} className="contents">
                    <dt className="font-mono text-ink-faint">{arg.name}</dt>
                    <dd className="m-0 [overflow-wrap:anywhere] text-ink-soft">{arg.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {call.modelReceives && (
              <pre className="mt-2 max-h-40 overflow-auto rounded-xs bg-sunken p-2 font-mono text-[11px] leading-snug whitespace-pre-wrap text-ink-soft [overflow-wrap:anywhere]">
                {pretty(call.modelReceives)}
              </pre>
            )}
            {call.logs && (
              // A code tool's own output: for whoever wrote it, not something the AI was told.
              <details className="mt-2 text-xs text-ink-soft">
                <summary className="cursor-pointer">Code output</summary>
                <pre className="mt-1.5 max-h-40 overflow-auto rounded-xs bg-sunken p-2 font-mono text-[11px] leading-snug whitespace-pre-wrap text-ink-soft [overflow-wrap:anywhere]">
                  {call.logs}
                </pre>
              </details>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

function pretty(json: string): string {
  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}
